"use client";

import { ICON_SIZES, Icon } from "@/components/library/Icon";
import {
  type BillingPeriod,
  type PricingPlan,
  type PricingPlanCtaType,
  type PricingPlanPrice,
} from "./pricingTypes";

import Link from "next/link";
import clsx from "clsx";
import { useBillingPeriod } from "./MarketingPricingProvider";
import { useDemoButtonLink } from "@/hooks/useDemoButtonText";
import { useLocalePath } from "@/hooks/useLocalePath";
import { withDemoSource } from "@/utils/demoSource";
import { useTrackDemoCtaClick } from "@/hooks/useDemoCtaTracking";

export type MarketingPricingCardsProps = {
  plans: PricingPlan[];
  className?: string;
};

function PriceDisplay({
  price,
  billingPeriod,
}: Readonly<{ price: PricingPlanPrice; billingPeriod: BillingPeriod }>) {
  const numberClass =
    "font-monument text-5xl font-medium leading-none tracking-tight text-black";
  const suffixClass = "font-monument text-sm text-black/70";

  if (price.kind === "free") {
    return (
      <div className="flex items-baseline gap-2">
        <span className={numberClass}>{price.label}</span>
        <span className={suffixClass}>{price.suffix}</span>
      </div>
    );
  }

  if (price.kind === "custom") {
    return <span className={numberClass}>{price.label}</span>;
  }

  const amount = billingPeriod === "yearly" ? price.yearly : price.monthly;

  return (
    <div className="flex items-baseline">
      <span className="font-monument text-2xl font-medium text-black">$</span>
      <span className={numberClass}>{amount}</span>
      <span className={clsx(suffixClass, "ml-2")}>{price.suffix}</span>
    </div>
  );
}

function PlanCta({
  label,
  ctaType,
  highlighted,
}: Readonly<{
  label: string;
  ctaType: PricingPlanCtaType;
  highlighted?: boolean;
}>) {
  const lp = useLocalePath();
  const demoLink = useDemoButtonLink(lp("/contact-sales"));
  const trackDemoCtaClick = useTrackDemoCtaClick();

  let href = lp("/signup");
  let target: "_blank" | "_self" = "_self";
  let isDemoCta = false;

  if (ctaType === "demo") {
    href =
      demoLink.target === "_self"
        ? withDemoSource(demoLink.href, "pricing_card")
        : demoLink.href;
    target = demoLink.target;
    isDemoCta = true;
  } else if (ctaType === "contactSales") {
    href = withDemoSource(lp("/contact-sales"), "pricing_card");
    isDemoCta = true;
  }

  return (
    <Link
      href={href}
      target={target}
      onClick={
        isDemoCta
          ? () =>
              trackDemoCtaClick({
                source: "pricing_card",
                placement: "pricing_card",
              })
          : undefined
      }
      className={clsx(
        "inline-flex w-full items-center justify-center rounded-lg px-5 py-2.5 font-monument text-sm font-medium transition-colors",
        highlighted
          ? "bg-gail-primary text-white hover:bg-gail-primary-hover"
          : "border border-black/10 bg-white text-black hover:bg-black/[0.03]",
      )}
    >
      {label}
    </Link>
  );
}

function PlanCard({
  plan,
  billingPeriod,
}: Readonly<{ plan: PricingPlan; billingPeriod: BillingPeriod }>) {
  const { highlighted } = plan;

  return (
    <div
      className={clsx(
        "relative flex flex-col rounded-2xl p-6",
        highlighted
          ? "bg-gail-primary-light ring-1 ring-gail-primary/30 lg:-my-4 lg:py-10"
          : "border border-black/5 bg-marketing-voice-card",
      )}
    >
      {plan.badge ? (
        <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gail-primary px-4 py-1 font-monument text-xs font-medium uppercase tracking-wide text-white">
          {plan.badge}
        </span>
      ) : null}

      <p
        className={clsx(
          "font-monument text-xs font-medium uppercase tracking-wide",
          highlighted ? "text-gail-primary" : "text-black/70",
        )}
      >
        {plan.audience}
      </p>
      <h3 className="mt-2 font-monument text-xl text-black">{plan.name}</h3>
      <p className="mt-2 font-monument text-sm leading-snug text-black/70 md:min-h-[3.75rem]">
        {plan.description}
      </p>

      <div className="mt-6 flex min-h-[60px] items-end">
        <PriceDisplay price={plan.price} billingPeriod={billingPeriod} />
      </div>

      <div className="mt-6">
        <PlanCta
          label={plan.ctaLabel}
          ctaType={plan.ctaType}
          highlighted={highlighted}
        />
      </div>

      <ul className="mt-8 flex flex-col gap-3">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <Icon
              icon="circle-check"
              size={ICON_SIZES.SM}
              className="mt-0.5 shrink-0 text-gail-primary"
            />
            <span className="font-monument text-sm leading-snug text-black/70">
              {feature}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MarketingPricingCards({
  plans,
  className,
}: Readonly<MarketingPricingCardsProps>) {
  const { billingPeriod } = useBillingPeriod();

  return (
    <section className={clsx("flex justify-center px-4 pt-6 pb-8", className)}>
      <div className="grid w-full max-w-6xl grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4 lg:items-stretch">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} billingPeriod={billingPeriod} />
        ))}
      </div>
    </section>
  );
}
